/**
 * MKS-168 (quick 260912-fs9) — URL-persisted board filter + search state.
 *
 * Two pure functions: `serializeBoardFiltersToParams` turns `BoardFilterState`
 * plus the search string into a plain key/value record (an active slot only
 * — a clean board keeps a clean URL); `parseBoardFiltersFromUrl` reads a
 * `URLSearchParams` back into the same shape.
 *
 * The param vocabulary is the SAME key vocabulary
 * `backend/src/api/schemas/boardQuery.ts` accepts (composite keys
 * `u:<id>` / `e:<id>` / `unassigned`, `s:<id>` / `triage`, `m:<id>` / `none`;
 * comma-separated id lists for `labels`/`tier`... `tier` has no client
 * control yet, so it is never emitted/parsed here) so a copy-pasted URL's
 * querystring reproduces the same view whether evaluated client-side
 * (`filterBoardColumns`) or server-side (`getBoardForProject`). The
 * client-only slots (`due`, `project`, `creator`) have no server counterpart
 * and are evaluated client-side either way. Per
 * `KanbanBoardPage.tsx`'s architecture decision, NO `sort` param is ever
 * emitted or parsed here — the UI owns no sort state in this change.
 *
 * A malformed value for any slot is DROPPED (never coerced, never a
 * wildcard match) — mirrors the server's no-fallthrough rule (T-fs9-03) and
 * `boardFilters.ts`'s existing T-134-03 precedent.
 */
import type { DateFilter, DateFilterMode, BoardFilterState } from './boardFilters';
import { EMPTY_BOARD_FILTERS, NO_CREATOR_KEY } from './boardFilters';
import type { TaskPriority } from '../services/api/board';

// Mirrors PriorityFilterToggle.tsx's local re-declaration (frontend has no
// exported TASK_PRIORITIES constant, unlike the backend's taskMutation.ts).
const TASK_PRIORITIES: readonly TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

const MAX_ID_LIST_LENGTH = 50;

/**
 * Every param key this module ever emits — used by callers (KanbanBoardPage)
 * to clear stale keys before re-serializing, so e.g. switching a date slot's
 * mode from `before` to `on` doesn't leave a stale `createdBefore` behind.
 */
export const BOARD_FILTER_URL_PARAM_KEYS = [
  'assignee',
  'state',
  'milestone',
  'priority',
  'labels',
  'createdBefore',
  'createdOn',
  'createdAfter',
  'activityBefore',
  'activityOn',
  'activityAfter',
  'due', // MKS-341
  'project', // MKS-419
  'creator', // MKS-510
  'q',
] as const;

// ---------------------------------------------------------------------------
// Composite-key validation (assignee / state / milestone) — no coercion, no
// wildcard fallthrough for a malformed value.
// ---------------------------------------------------------------------------

function isValidPositiveIntAfterPrefix(value: string, prefix: string): boolean {
  if (!value.startsWith(prefix)) return false;
  const rest = value.slice(prefix.length);
  if (!/^\d+$/.test(rest)) return false;
  const n = Number(rest);
  return Number.isInteger(n) && n > 0;
}

function isValidAssigneeKey(value: string): boolean {
  return (
    value === 'unassigned' ||
    isValidPositiveIntAfterPrefix(value, 'u:') ||
    isValidPositiveIntAfterPrefix(value, 'e:')
  );
}

/**
 * MKS-510 — the creator slot's vocabulary: the same `u:<id>` / `e:<id>`
 * composite keys as the assignee slot, plus its own no-creator sentinel. The
 * server knows no `creator` param (the slot is client-side only), so this
 * validator, not `boardQuery.ts`, is its whole definition.
 */
function isValidCreatorKey(value: string): boolean {
  return (
    value === NO_CREATOR_KEY ||
    isValidPositiveIntAfterPrefix(value, 'u:') ||
    isValidPositiveIntAfterPrefix(value, 'e:')
  );
}

function isValidStateKey(value: string): boolean {
  return value === 'triage' || isValidPositiveIntAfterPrefix(value, 's:');
}

function isValidMilestoneKey(value: string): boolean {
  return value === 'none' || isValidPositiveIntAfterPrefix(value, 'm:');
}

// ---------------------------------------------------------------------------
// Comma-separated positive-int list (labels) — bounded, no partial matches.
// ---------------------------------------------------------------------------

function parsePositiveIntList(value: string): number[] | null {
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (parts.length === 0 || parts.length > MAX_ID_LIST_LENGTH) return null;
  const ids: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return null;
    const n = Number(part);
    if (!Number.isInteger(n) || n <= 0) return null;
    ids.push(n);
  }
  return ids;
}

function parsePriorityList(value: string): TaskPriority[] | null {
  const parts = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (parts.length === 0) return null;
  const values: TaskPriority[] = [];
  for (const part of parts) {
    if (!(TASK_PRIORITIES as readonly string[]).includes(part)) return null;
    values.push(part as TaskPriority);
  }
  return values;
}

// ---------------------------------------------------------------------------
// Date-only (day-precision) axes — createdBefore/On/After,
// activityBefore/On/After. Reuses boardFilters.ts's day-precision contract:
// a day filter matches the whole calendar day.
// ---------------------------------------------------------------------------

const DAY_STRING_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidDayString(value: string): boolean {
  return DAY_STRING_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`));
}

function dateParamKey(axis: 'created' | 'activity', mode: DateFilterMode): string {
  const capitalizedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
  return `${axis}${capitalizedMode}`;
}

const DATE_FILTER_MODES: readonly DateFilterMode[] = ['before', 'on', 'after'];

function parseDateAxis(params: URLSearchParams, axis: 'created' | 'activity'): DateFilter | null {
  for (const mode of DATE_FILTER_MODES) {
    const raw = params.get(dateParamKey(axis, mode));
    if (raw != null && isValidDayString(raw)) {
      return { mode, date: raw };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// serialize
// ---------------------------------------------------------------------------

/**
 * Emits a param only for an ACTIVE slot (per `countActiveBoardFilters`'s own
 * notion of "active": a date slot with a blank date is inactive). Returns a
 * plain record suitable for merging into existing `URLSearchParams` via
 * `setSearchParams`.
 */
export function serializeBoardFiltersToParams(
  filters: BoardFilterState,
  searchQuery: string,
): Record<string, string> {
  const params: Record<string, string> = {};

  if (filters.assignee != null) params.assignee = filters.assignee;
  if (filters.status != null) params.state = filters.status;
  if (filters.milestone != null) params.milestone = filters.milestone;
  if (filters.priority.length > 0) params.priority = filters.priority.join(',');
  if (filters.labels.length > 0) params.labels = filters.labels.map(String).join(',');
  if (filters.due != null) params.due = filters.due; // MKS-341
  if (filters.subproject != null) params.project = filters.subproject; // MKS-419
  if (filters.creator != null) params.creator = filters.creator; // MKS-510

  if (filters.created != null && filters.created.date !== '') {
    params[dateParamKey('created', filters.created.mode)] = filters.created.date;
  }
  if (filters.updated != null && filters.updated.date !== '') {
    params[dateParamKey('activity', filters.updated.mode)] = filters.updated.date;
  }

  // Mirrors KanbanBoardPage's own `searchActive` test (normalizeSearchText
  // emptiness), so a whitespace-only query is treated as inactive here too.
  if (searchQuery.trim() !== '') params.q = searchQuery;

  return params;
}

// ---------------------------------------------------------------------------
// parse
// ---------------------------------------------------------------------------

export interface ParsedBoardFilterUrl {
  filters: BoardFilterState;
  searchQuery: string;
}

/**
 * Parses ONLY the param keys this module owns — any other param (e.g. the
 * page's own `openTask`) is left untouched by the caller, since this
 * function never mutates the input and never reads/returns unrelated keys.
 * A malformed value for any slot yields the inactive slot, never a crash.
 */
export function parseBoardFiltersFromUrl(searchParams: URLSearchParams): ParsedBoardFilterUrl {
  const filters: BoardFilterState = { ...EMPTY_BOARD_FILTERS };

  const assignee = searchParams.get('assignee');
  if (assignee != null && isValidAssigneeKey(assignee)) filters.assignee = assignee;

  const state = searchParams.get('state');
  if (state != null && isValidStateKey(state)) filters.status = state;

  const milestone = searchParams.get('milestone');
  if (milestone != null && isValidMilestoneKey(milestone)) filters.milestone = milestone;

  const priority = searchParams.get('priority');
  if (priority != null) {
    const parsed = parsePriorityList(priority);
    if (parsed != null) filters.priority = parsed;
  }

  const labels = searchParams.get('labels');
  if (labels != null) {
    const parsed = parsePositiveIntList(labels);
    if (parsed != null) filters.labels = parsed;
  }

  // MKS-341 — exact-match against the known modes, no coercion: a crafted
  // `?due=` value leaves the slot inactive rather than matching nothing (or,
  // worse, everything). Same no-wildcard-fallthrough rule as the composite
  // keys above.
  const due = searchParams.get('due');
  if (due === 'overdue' || due === 'this_week' || due === 'none') filters.due = due;

  // MKS-419 — `p:<positive int>` only; `12`, `p:0`, `p:x` leave the slot inactive.
  const project = searchParams.get('project');
  if (project != null && isValidPositiveIntAfterPrefix(project, 'p:')) {
    filters.subproject = project;
  }

  // MKS-510 — an unknown or stale value (a renamed key, a link shared from a
  // board the reader cannot see, a crafted string) leaves the slot INACTIVE:
  // never a throw, and never an active key that would hide every card.
  const creator = searchParams.get('creator');
  if (creator != null && isValidCreatorKey(creator)) filters.creator = creator;

  const created = parseDateAxis(searchParams, 'created');
  if (created != null) filters.created = created;

  const updated = parseDateAxis(searchParams, 'activity');
  if (updated != null) filters.updated = updated;

  const searchQuery = searchParams.get('q') ?? '';

  return { filters, searchQuery };
}
